/**
 * Lectura y validacion tipada de la configuracion de la web.
 *
 * `leerConfiguracion` es una funcion pura: recibe el registro de variables en lugar de
 * leer `import.meta.env` por su cuenta. Eso permite probar los casos de fallo sin
 * inventar entornos, y es lo que hace que el mensaje de error sea verificable.
 *
 * Reglas que aplica:
 *   - En modo emulador (ADR-032) no hace falta configuracion de Firebase.
 *   - Fuera del emulador se exige TODA la configuracion, App Check incluido (ADR-028).
 *   - Un placeholder sin reemplazar, del tipo `<FIREBASE_API_KEY>`, se rechaza igual
 *     que si faltara: los marcadores deben fallar de forma ruidosa (CLAUDE.md §5).
 */

import { fallo, exito, type Resultado } from '@padel/domain';

/** Variables exigidas cuando la app NO apunta a emuladores. */
const VARIABLES_DE_PRODUCCION = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
  'VITE_FIREBASE_STORAGE_BUCKET',
  'VITE_FIREBASE_MESSAGING_SENDER_ID',
  'VITE_FIREBASE_APP_ID',
  'VITE_APPCHECK_RECAPTCHA_SITE_KEY',
] as const;

/** Un valor de la forma `<ALGO_ASI>` es un placeholder de .env.example sin reemplazar. */
const PLACEHOLDER = /^<[A-Z0-9_]+>$/;

/** Credenciales publicas del proyecto Firebase. */
export interface ConfiguracionFirebase {
  readonly apiKey: string;
  readonly authDomain: string;
  readonly projectId: string;
  readonly storageBucket: string;
  readonly messagingSenderId: string;
  readonly appId: string;
}

/** Configuracion validada de la aplicacion. */
export interface ConfiguracionApp {
  readonly usarEmuladores: boolean;
  readonly emuladoresHost: string;
  /** `null` en modo emulador: no hay proyecto real al que apuntar. */
  readonly firebase: ConfiguracionFirebase | null;
  /** `null` en modo emulador: App Check no se exige contra emuladores. */
  readonly appCheckSiteKey: string | null;
  /**
   * Token de depuracion de App Check (ADR-028, docs/SEGURIDAD.md §4).
   *
   * Es el **unico** mecanismo admitido para probar contra un proyecto real desde una
   * maquina de desarrollo, y vive solo en `apps/web/.env`, que nunca se versiona.
   * `null` es lo normal: en modo emulador no hay App Check que depurar.
   */
  readonly appCheckDebugToken: string | null;
  /** Número público para consultas; `null` mantiene oculto el botón flotante. */
  readonly whatsappConsultasNumero: string | null;
}

/** Contexto de ejecucion en el que se lee la configuracion. */
export interface OpcionesLectura {
  /** `true` con el servidor de desarrollo de Vite; `false` en una compilacion. */
  readonly esDesarrollo: boolean;
}

/** Fallo de configuracion, con lo necesario para que la persona lo resuelva sola. */
export interface ErrorConfiguracion {
  readonly codigo: 'CONFIGURACION_INCOMPLETA';
  readonly faltantes: readonly string[];
  readonly placeholders: readonly string[];
  readonly mensaje: string;
}

function leerTexto(bruto: Record<string, unknown>, clave: string): string {
  const valor = bruto[clave];
  return typeof valor === 'string' ? valor.trim() : '';
}

/**
 * Normaliza el número público al formato de `wa.me`: código de país y número, solo
 * dígitos. Acepta separadores humanos en la variable, pero nunca inventa el prefijo.
 */
export function normalizarNumeroWhatsappConsultas(valor: unknown): string | null {
  if (typeof valor !== 'string') return null;
  const recortado = valor.trim();
  if (recortado === '' || PLACEHOLDER.test(recortado)) return null;
  if (!/^\+?[0-9\s().-]+$/.test(recortado)) return null;

  const digitos = recortado.replace(/\D/g, '');
  return /^[1-9]\d{7,14}$/.test(digitos) ? digitos : null;
}

function construirMensaje(faltantes: readonly string[], placeholders: readonly string[]): string {
  const partes: string[] = ['La aplicacion no puede arrancar porque le falta configuracion.', ''];

  if (faltantes.length > 0) {
    partes.push(
      `Variables sin definir (${String(faltantes.length)}):`,
      ...faltantes.map((nombre) => `  - ${nombre}`),
      '',
    );
  }

  if (placeholders.length > 0) {
    partes.push(
      `Variables que todavia tienen el placeholder de .env.example (${String(placeholders.length)}):`,
      ...placeholders.map((nombre) => `  - ${nombre}`),
      '',
    );
  }

  partes.push(
    'Como resolverlo:',
    '  1. Copia apps/web/.env.example a apps/web/.env',
    '       macOS/Linux (zsh)     cp apps/web/.env.example apps/web/.env',
    '       Windows (PowerShell)  Copy-Item apps\\web\\.env.example apps\\web\\.env',
    '  2. Para desarrollo local, deja VITE_USAR_EMULADORES=true y no hace falta nada mas.',
    '  3. Para apuntar a un proyecto real, completa los valores desde la consola de',
    '     Firebase del proyecto correspondiente.',
    '',
    'Nunca versiones apps/web/.env: esta ignorado por Git a proposito (ADR-031).',
  );

  return partes.join('\n');
}

/**
 * Valida la configuracion de la aplicacion.
 *
 * @param bruto Registro de variables, normalmente `import.meta.env`.
 * @param opciones Contexto de ejecucion. Se pasa desde fuera para que la funcion
 *   siga siendo pura y se pueda probar cualquier combinacion.
 */
export function leerConfiguracion(
  bruto: Record<string, unknown>,
  opciones: OpcionesLectura = { esDesarrollo: false },
): Resultado<ConfiguracionApp, ErrorConfiguracion> {
  const valorEmuladores = leerTexto(bruto, 'VITE_USAR_EMULADORES');

  // Sin la variable definida: en desarrollo se asume emuladores, porque es lo que
  // manda ADR-032 y porque un clon recien bajado tiene que arrancar con `npm run dev`
  // sin configurar nada. En una compilacion de produccion el valor por defecto es el
  // seguro: exigir la configuracion completa y fallar de forma ruidosa si falta.
  const usarEmuladores =
    valorEmuladores === '' ? opciones.esDesarrollo : valorEmuladores === 'true';
  const emuladoresHost = leerTexto(bruto, 'VITE_EMULADORES_HOST') || '127.0.0.1';
  const debugAppCheck = leerTexto(bruto, 'VITE_APPCHECK_DEBUG_TOKEN');
  const appCheckDebugToken =
    debugAppCheck === '' || PLACEHOLDER.test(debugAppCheck) ? null : debugAppCheck;
  const whatsappConsultasNumero = normalizarNumeroWhatsappConsultas(
    bruto['VITE_WHATSAPP_CONSULTAS_NUMERO'],
  );

  if (usarEmuladores) {
    return exito({
      usarEmuladores: true,
      emuladoresHost,
      firebase: null,
      appCheckSiteKey: null,
      appCheckDebugToken,
      whatsappConsultasNumero,
    });
  }

  const faltantes: string[] = [];
  const placeholders: string[] = [];

  for (const nombre of VARIABLES_DE_PRODUCCION) {
    const valor = leerTexto(bruto, nombre);
    if (valor === '') {
      faltantes.push(nombre);
    } else if (PLACEHOLDER.test(valor)) {
      placeholders.push(nombre);
    }
  }

  if (faltantes.length > 0 || placeholders.length > 0) {
    return fallo({
      codigo: 'CONFIGURACION_INCOMPLETA',
      faltantes,
      placeholders,
      mensaje: construirMensaje(faltantes, placeholders),
    });
  }

  return exito({
    usarEmuladores: false,
    emuladoresHost,
    firebase: {
      apiKey: leerTexto(bruto, 'VITE_FIREBASE_API_KEY'),
      authDomain: leerTexto(bruto, 'VITE_FIREBASE_AUTH_DOMAIN'),
      projectId: leerTexto(bruto, 'VITE_FIREBASE_PROJECT_ID'),
      storageBucket: leerTexto(bruto, 'VITE_FIREBASE_STORAGE_BUCKET'),
      messagingSenderId: leerTexto(bruto, 'VITE_FIREBASE_MESSAGING_SENDER_ID'),
      appId: leerTexto(bruto, 'VITE_FIREBASE_APP_ID'),
    },
    appCheckSiteKey: leerTexto(bruto, 'VITE_APPCHECK_RECAPTCHA_SITE_KEY'),
    appCheckDebugToken,
    whatsappConsultasNumero,
  });
}
